import React, { FC } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import moment from 'moment';

import { EditIcon, SingleDatePicker, TrashIcon } from 'src/shared/components';
import { DAY_COMMA_DATE_FORMAT, DAY_FULL_FORMAT } from 'src/shared/constants';
import { CycleTypeEnums, PermissionsEnums } from 'src/shared/enums';
import { ComponentWithPermissions } from 'src/shared/hocs';
import { useTranslate } from 'src/shared/providers';

import { AvailabilityCycleInputsType } from './availabilities.types';
import { mapCycleTypeEnum } from './availabilities.utils';

interface Props {
  cycle: AvailabilityCycleInputsType | undefined;
  loading?: boolean;
  updating?: boolean;
  isEditMode?: boolean;
  readOnly?: boolean;
  onEdit?: () => void;
  onStartDateChange?: (date: moment.Moment | null) => void;
  onCancel?: () => void;
  onSave?: () => void;
  onDelete?: () => void;
}

export const EmployeeAvailabilityCycleInfoTable: FC<Props> = ({
  cycle,
  loading,
  updating,
  isEditMode,
  readOnly,
  onEdit,
  onStartDateChange,
  onCancel,
  onSave,
  onDelete,
}) => {
  const { t } = useTranslate();

  if (loading || !cycle) {
    return <Skeleton className="rounded" variant="rectangular" animation="wave" height="72px" />;
  }

  return (
    <Stack>
      <TableContainer sx={{ border: 'none', td: { verticalAlign: 'middle' }, 'td, th': { border: 'none', p: 1 } }}>
        <Table padding="none" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell width="20%">
                <Typography variant="body2" fontWeight={500}>
                  {t('global.type')}
                </Typography>
              </TableCell>
              <TableCell width="30%">
                <Typography variant="body2" fontWeight={500}>
                  {cycle.type === CycleTypeEnums.DAYS ? t('global.length') : t('availability.firstDayOfCycle')}
                </Typography>
              </TableCell>
              <TableCell colSpan={2}>
                {!isEditMode && (
                  <Typography variant="body2" fontWeight={500}>
                    {t('availability.startDate')}
                  </Typography>
                )}
              </TableCell>
              <TableCell width="15%">
                <ComponentWithPermissions permissions={PermissionsEnums.WORK_TIME_PREFERENCES_EDIT}>
                  {!readOnly && !isEditMode && !cycle.isLocked ? (
                    <Stack direction="row" alignItems="center" id="edit-cell">
                      <IconButton onClick={onEdit}>
                        <EditIcon size={20} />
                      </IconButton>
                      <IconButton color="error" onClick={onDelete}>
                        <TrashIcon size={20} />
                      </IconButton>
                    </Stack>
                  ) : null}
                </ComponentWithPermissions>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ td: { verticalAlign: 'top' } }}>
            <TableRow>
              <TableCell width="15%">
                <Typography variant="body2" color="text.secondary">
                  {mapCycleTypeEnum(cycle.type, t)}
                </Typography>
              </TableCell>
              <TableCell width="30%">
                <Typography variant="body2" color="text.secondary">
                  {cycle.type === CycleTypeEnums.DAYS
                    ? cycle.cycleLength
                    : moment(cycle.cycleStart).format(DAY_FULL_FORMAT)}
                </Typography>
              </TableCell>
              <TableCell colSpan={2}>
                {!isEditMode && (
                  <Typography variant="body2" color="text.secondary">
                    {moment(cycle.validFrom).format(DAY_COMMA_DATE_FORMAT)}
                  </Typography>
                )}
              </TableCell>
              <TableCell width="15%" />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {isEditMode && (
        <>
          <TableContainer sx={{ border: 'none', 'td, th': { border: 'none', p: 1 } }}>
            <Table padding="none" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {t('availability.startDate')}
                    </Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody sx={{ td: { verticalAlign: 'top' } }}>
                <TableRow>
                  <TableCell>
                    <SingleDatePicker
                      minDate={moment()}
                      value={moment(cycle.validFrom)}
                      onChange={onStartDateChange}
                      placeholder={t('absences.selectDate')}
                      disabled={updating}
                    />
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" alignItems="center" gap={2} paddingLeft={1} marginTop={2}>
            <Button color="secondary" onClick={onCancel}>
              {t('global.cancel')}
            </Button>
            <LoadingButton variant="contained" onClick={onSave} loading={updating}>
              {t('global.save')}
            </LoadingButton>
          </Stack>
        </>
      )}
    </Stack>
  );
};
